import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCircleInfo, faPlus, faWallet } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import { formatShortDate } from '../../utils/financeWorkflow';

const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';

const PAYMENT_METHOD_OPTIONS = ['Bank Transfer', 'MPESA', 'Paybill', 'Till Number', 'Cash', 'Cheque', 'Other'];
const VENDOR_STATUS_OPTIONS = ['Active', 'Inactive'];

const normalizeVendorStatus = (status) => {
  const normalized = String(status || 'Active').trim();
  return normalized || 'Active';
};

const normalizeVendorPaymentMethod = (method) => {
  const normalized = String(method || '').trim();

  if (!normalized) {
    return 'Not set';
  }

  return PAYMENT_METHOD_OPTIONS.includes(normalized) ? normalized : normalized;
};

const getVendorStatusTone = (status) => (
  normalizeVendorStatus(status) === 'Active' ? 'success' : 'muted'
);

const buildEmptyForm = () => ({
  branch: '',
  vendorName: '',
  vendorServices: '',
  vendorContactPerson: '',
  vendorEmail: '',
  vendorPhone: '',
  vendorStatus: 'Active',
  serviceDesc: '',
  commDate: '',
  vendorPaymentMethod: PAYMENT_METHOD_OPTIONS[0],
  vendorPaymentBankId: '',
  vendorPaymentBankName: '',
  vendorPaymentBankCode: '',
  vendorPaymentReference: '',
  vendorPaymentPhone: '',
  vendorPaymentPaybillNumber: '',
  vendorPaymentPaybillAccountNumber: '',
  vendorPaymentTillNumber: '',
  paymentDetails: ''
});

const mapLotusBankOptions = (payload) => {
  const rawBanks = payload?.data?.banks || payload?.data?.bank_list || payload?.banks || [];

  if (!Array.isArray(rawBanks)) {
    return [];
  }

  return rawBanks
    .map((bank) => {
      const bankId = String(bank?.id || bank?.bank_id || bank?.bankId || bank?.code || bank?.bank_code || '').trim();
      const bankName = String(
        bank?.name || bank?.bank_name || bank?.bankName || bank?.display_name || bank?.label || bankId || 'Bank'
      ).trim();

      return {
        id: bankId || bankName,
        name: bankName,
        code: String(bank?.code || bank?.bank_code || bank?.bankCode || '').trim(),
        raw: bank
      };
    })
    .filter((bank) => bank.id || bank.name);
};

const getVendorPayoutSummary = (vendor = {}) => {
  const method = normalizeVendorPaymentMethod(vendor.vendorPaymentMethod || vendor.paymentMethod);

  if (method === 'Bank Transfer') {
    return {
      method,
      tone: 'success',
      detail: [
        vendor.vendorPaymentBankName || 'Bank not set',
        vendor.vendorPaymentReference || 'Account number not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(String(vendor.vendorPaymentBankId || '').trim() && String(vendor.vendorPaymentReference || '').trim())
    };
  }

  if (method === 'MPESA') {
    return {
      method,
      tone: 'warning',
      detail: vendor.vendorPaymentPhone || 'Phone not set',
      ready: Boolean(String(vendor.vendorPaymentPhone || '').trim())
    };
  }

  if (method === 'Paybill') {
    return {
      method,
      tone: 'info',
      detail: [
        vendor.vendorPaymentPaybillNumber || 'Paybill not set',
        vendor.vendorPaymentPaybillAccountNumber || 'Account not set'
      ].filter(Boolean).join(' • '),
      ready: Boolean(
        String(vendor.vendorPaymentPaybillNumber || '').trim()
        && String(vendor.vendorPaymentPaybillAccountNumber || '').trim()
      )
    };
  }

  if (method === 'Till Number') {
    return {
      method,
      tone: 'info',
      detail: vendor.vendorPaymentTillNumber || 'Till number not set',
      ready: Boolean(String(vendor.vendorPaymentTillNumber || '').trim())
    };
  }

  if (method === 'Cash') {
    return {
      method,
      tone: 'muted',
      detail: vendor.vendorPaymentReference || 'Cash payout',
      ready: true
    };
  }

  if (method === 'Cheque') {
    return {
      method,
      tone: 'danger',
      detail: vendor.vendorPaymentReference || 'Cheque details not set',
      ready: true
    };
  }

  if (method === 'Not set') {
    return {
      method,
      tone: 'muted',
      detail: 'No payout method set',
      ready: false
    };
  }

  return {
    method,
    tone: 'muted',
    detail: vendor.vendorPaymentReference || 'Payout method set',
    ready: true
  };
};

const isVendorPayoutReady = (vendor = {}) => getVendorPayoutSummary(vendor).ready;

function VendorsComp() {
  const auth = getAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [detailsModalShow, setDetailsModalShow] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [methodFilter, setMethodFilter] = useState('All');
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [bankLoadError, setBankLoadError] = useState('');
  const [formState, setFormState] = useState(buildEmptyForm());

  const loadLotusBanks = useCallback(async ({ force = false } = {}) => {
    if ((banksLoaded && !force) || loadingBanks) {
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setBankLoadError('Sign in again to load bank options.');
      return;
    }

    setLoadingBanks(true);
    setBankLoadError('');

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${LOTUS_API_BASE_URL}/banks`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to load banks.');
      }

      setBankOptions(mapLotusBankOptions(payload));
      setBanksLoaded(true);
    } catch (error) {
      console.error('Failed to load Lotus banks:', error);
      setBankLoadError('Banks could not be loaded right now.');
      swal('Bank list unavailable', 'We could not load the Lotus bank list right now.', 'warning');
    } finally {
      setLoadingBanks(false);
    }
  }, [auth, banksLoaded, loadingBanks]);

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);
        const [vendorSnapshot, branchSnapshot] = await Promise.all([
          getDocs(collection(db, 'Vendors')),
          getDocs(collection(db, 'Branches'))
        ]);

        const nextVendors = vendorSnapshot.docs
          .map((vendorDoc) => ({
            id: vendorDoc.id,
            ...vendorDoc.data()
          }))
          .sort((first, second) => {
            const firstTime = first.timeCreated?.seconds || 0;
            const secondTime = second.timeCreated?.seconds || 0;
            return secondTime - firstTime;
          });

        const nextBranches = branchSnapshot.docs.map((branchDoc) => ({
          id: branchDoc.id,
          ...branchDoc.data()
        }));

        setVendors(nextVendors);
        setBranches(nextBranches);
      } catch (error) {
        console.error('Failed to load vendors:', error);
        swal('Error', 'Vendors could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  useEffect(() => {
    if (branches.length === 0) {
      return;
    }

    setFormState((currentForm) => (
      currentForm.branch
        ? currentForm
        : {
            ...currentForm,
            branch: branches[0]?.id || 'General'
          }
    ));
  }, [branches]);

  useEffect(() => {
    if (modalShow && formState.vendorPaymentMethod === 'Bank Transfer' && !banksLoaded) {
      loadLotusBanks();
    }
  }, [banksLoaded, formState.vendorPaymentMethod, loadLotusBanks, modalShow]);

  const branchMap = useMemo(() => {
    return branches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});
  }, [branches]);

  const filteredVendors = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return vendors.filter((vendor) => {
      const branchName =
        vendor.branch === 'General'
          ? 'General'
          : branchMap[vendor.branch] || 'Campus not set';
      const vendorStatus = normalizeVendorStatus(vendor.vendorStatus || vendor.status);
      const paymentSummary = getVendorPayoutSummary(vendor);

      if (
        branchFilter !== 'All'
        && branchFilter !== vendor.branch
        && !(branchFilter === 'General' && vendor.branch === 'General')
      ) {
        return false;
      }

      if (statusFilter !== 'All' && vendorStatus !== statusFilter) {
        return false;
      }

      if (methodFilter !== 'All' && paymentSummary.method !== methodFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        vendor.vendorName,
        vendor.vendorContactPerson,
        vendor.vendorServices,
        vendor.vendorEmail,
        vendor.vendorPhone,
        vendor.serviceDesc,
        vendor.paymentDetails,
        vendor.vendorPaymentMethod,
        vendor.vendorPaymentBankName,
        vendor.vendorPaymentReference,
        vendor.vendorPaymentPhone,
        vendor.vendorPaymentPaybillNumber,
        vendor.vendorPaymentPaybillAccountNumber,
        vendor.vendorPaymentTillNumber,
        vendorStatus,
        branchName
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [branchFilter, branchMap, methodFilter, searchTerm, statusFilter, vendors]);

  const summary = useMemo(() => {
    const activeVendors = vendors.filter((vendor) => normalizeVendorStatus(vendor.vendorStatus || vendor.status) === 'Active').length;
    const payoutReadyVendors = vendors.filter((vendor) => isVendorPayoutReady(vendor)).length;

    return {
      totalVendors: vendors.length,
      activeVendors,
      payoutReadyVendors
    };
  }, [vendors]);

  const resetFilters = () => {
    setSearchTerm('');
    setBranchFilter('All');
    setStatusFilter('All');
    setMethodFilter('All');
  };

  const resetForm = () => {
    setFormState({
      ...buildEmptyForm(),
      branch: branches[0]?.id || 'General'
    });
  };

  const closeModal = () => {
    if (submitting) {
      return;
    }

    setModalShow(false);
  };

  const closeDetailsModal = () => {
    setDetailsModalShow(false);
    setSelectedVendor(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormState((currentForm) => {
      if (name === 'vendorPaymentMethod') {
        return {
          ...currentForm,
          [name]: value,
          vendorPaymentBankId: '',
          vendorPaymentBankName: '',
          vendorPaymentBankCode: '',
          vendorPaymentReference: '',
          vendorPaymentPhone: '',
          vendorPaymentPaybillNumber: '',
          vendorPaymentPaybillAccountNumber: '',
          vendorPaymentTillNumber: ''
        };
      }

      return {
        ...currentForm,
        [name]: value
      };
    });
  };

  const handleBankSelect = (event) => {
    const selectedBankId = event.target.value;
    const selectedBank = bankOptions.find((bank) => bank.id === selectedBankId) || null;

    setFormState((currentForm) => ({
      ...currentForm,
      vendorPaymentBankId: selectedBank?.id || '',
      vendorPaymentBankName: selectedBank?.name || '',
      vendorPaymentBankCode: selectedBank?.code || ''
    }));
  };

  const handleSubmitVendor = async () => {
    const vendorName = formState.vendorName.trim();
    const vendorServices = formState.vendorServices.trim();
    const paymentMethod = normalizeVendorPaymentMethod(formState.vendorPaymentMethod);
    const paymentReference = formState.vendorPaymentReference.trim();
    const paymentPhone = formState.vendorPaymentPhone.trim();
    const paybillNumber = formState.vendorPaymentPaybillNumber.trim();
    const paybillAccountNumber = formState.vendorPaymentPaybillAccountNumber.trim();
    const tillNumber = formState.vendorPaymentTillNumber.trim();

    if (!vendorName || !vendorServices) {
      swal('Missing details', 'Vendor name and service line are required.', 'warning');
      return;
    }

    if (paymentMethod === 'Bank Transfer') {
      if (!formState.vendorPaymentBankId.trim() || !paymentReference) {
        swal(
          'Missing payout details',
          'Select a bank from Lotus and add the vendor bank account number.',
          'warning'
        );
        return;
      }
    }

    if (paymentMethod === 'MPESA' && !paymentPhone) {
      swal('Missing payout details', 'Add the vendor MPESA phone number.', 'warning');
      return;
    }

    if (paymentMethod === 'Paybill' && (!paybillNumber || !paybillAccountNumber)) {
      swal('Missing payout details', 'Add the vendor paybill number and paybill account number.', 'warning');
      return;
    }

    if (paymentMethod === 'Till Number' && !tillNumber) {
      swal('Missing payout details', 'Add the vendor till number.', 'warning');
      return;
    }

    setSubmitting(true);

    try {
      const vendorRef = doc(collection(db, 'Vendors'));
      await setDoc(vendorRef, {
        branch: formState.branch || 'General',
        vendorName,
        vendorServices,
        vendorContactPerson: formState.vendorContactPerson.trim(),
        vendorEmail: formState.vendorEmail.trim(),
        vendorPhone: formState.vendorPhone.trim(),
        vendorStatus: normalizeVendorStatus(formState.vendorStatus),
        serviceDesc: formState.serviceDesc.trim(),
        commDate: formState.commDate ? new Date(formState.commDate) : null,
        vendorPaymentMethod: paymentMethod,
        vendorPaymentBankId: formState.vendorPaymentBankId.trim(),
        vendorPaymentBankName: formState.vendorPaymentBankName.trim(),
        vendorPaymentBankCode: formState.vendorPaymentBankCode.trim(),
        vendorPaymentReference: paymentReference,
        vendorPaymentPhone: paymentPhone,
        vendorPaymentPaybillNumber: paybillNumber,
        vendorPaymentPaybillAccountNumber: paybillAccountNumber,
        vendorPaymentTillNumber: tillNumber,
        paymentDetails: formState.paymentDetails.trim(),
        timeCreated: serverTimestamp(),
        docID: vendorRef.id
      });

      swal('Success', 'Vendor added successfully.', 'success');
      setModalShow(false);
      resetForm();

      const vendorSnapshot = await getDocs(collection(db, 'Vendors'));
      const nextVendors = vendorSnapshot.docs
        .map((vendorDoc) => ({
          id: vendorDoc.id,
          ...vendorDoc.data()
        }))
        .sort((first, second) => {
          const firstTime = first.timeCreated?.seconds || 0;
          const secondTime = second.timeCreated?.seconds || 0;
          return secondTime - firstTime;
        });
      setVendors(nextVendors);
    } catch (error) {
      console.error('Failed to add vendor:', error);
      swal('Error', 'Vendor could not be added right now.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="clientsComp applicationsPage vendorsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Finance workflow</p>
          <h4>Vendors</h4>
          <p className="appsSubtext">Manage supplier records, payout methods, and the details used for settlements.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Total</span>
            <strong>{summary.totalVendors}</strong>
          </div>
          <div className="appsStatChip">
            <span>Active</span>
            <strong>{summary.activeVendors}</strong>
          </div>
          <div className="appsStatChip">
            <span>Ready</span>
            <strong>{summary.payoutReadyVendors}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard vendorsFiltersCard">
        <div className="appsSearch input-group vendorsSearch">
          <span className="input-group-text">
            <FontAwesomeIcon icon={faBuilding} />
          </span>
          <Form.Control
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search vendor, contact, payout method..."
          />
        </div>
        <div className="appsFilterGroup vendorsFilterGroup">
          <Form.Select className="filterSelect" value={branchFilter} onChange={(event) => setBranchFilter(event.target.value)}>
            <option value="All">All campuses</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.branchName}
              </option>
            ))}
            <option value="General">General</option>
          </Form.Select>
          <Form.Select className="filterSelect" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="All">All statuses</option>
            {VENDOR_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Form.Select>
          <Form.Select className="filterSelect" value={methodFilter} onChange={(event) => setMethodFilter(event.target.value)}>
            <option value="All">All payment methods</option>
            {PAYMENT_METHOD_OPTIONS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </Form.Select>
          <button type="button" className="opsPrimaryBtn vendorsAddBtn" onClick={() => setModalShow(true)}>
            <FontAwesomeIcon icon={faPlus} /> Add vendor
          </button>
        </div>
      </div>

      <div className="appsTableCard vendorsTableCard">
        <div className="appsTableHeader">
          <h6>Supplier directory</h6>
          <span>{filteredVendors.length} results</span>
        </div>
        <div className="appsTableWrap">
          {loading ? (
            <div className="opsLoadingCard vendorsLoadingCard">
              <Spinner animation="border" role="status" />
              <span>Loading vendors...</span>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="appsEmptyState">No vendors found. Try a different filter or add the first vendor.</div>
          ) : (
            <Table hover responsive className="appsTable vendorsTable">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Campus</th>
                  <th>Contact</th>
                  <th>Payout</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((vendor) => {
                  const branchName =
                    vendor.branch === 'General'
                      ? 'General'
                      : branchMap[vendor.branch] || 'Campus not set';
                  const vendorStatus = normalizeVendorStatus(vendor.vendorStatus || vendor.status);
                  const paymentSummary = getVendorPayoutSummary(vendor);

                  return (
                    <tr key={vendor.id}>
                      <td>
                        <div className="appsStudentCell vendorsVendorCell">
                          <div>
                            <p className="appsStudentName">{vendor.vendorName || 'Vendor'}</p>
                            <span className="appsStudentMeta">
                              {vendor.vendorContactPerson || vendor.vendorServices || 'No description yet'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>{branchName}</td>
                      <td>
                        <div className="vendorsCompactMeta">
                          <strong>{vendor.vendorPhone || 'Phone not set'}</strong>
                          <span>{vendor.vendorEmail || 'Email not set'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="vendorsCompactMeta">
                          <strong>{paymentSummary.method}</strong>
                          <span>{paymentSummary.detail}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`opsStatusPill opsStatusPill--${getVendorStatusTone(vendorStatus)}`}>
                          {vendorStatus}
                        </span>
                      </td>
                      <td>{formatShortDate(vendor.commDate, 'Not set')}</td>
                      <td className="vendorsTableActionCell">
                        <button
                          type="button"
                          className="opsGhostBtn vendorsTableBtn"
                          onClick={() => {
                            navigate(`/view-vendor/${vendor.id}`);
                          }}
                        >
                          View vendor
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal show={modalShow} onHide={closeModal} centered size="lg" dialogClassName="vendorsModalDialog">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Add vendor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="opsModalCopyBlock mb-3">
            <span>
              <FontAwesomeIcon icon={faCircleInfo} /> Vendor setup
            </span>
            <p>
              Add the vendor profile first, then define the payout method so the team can use the same record for billing
              and settlement.
            </p>
          </div>

          <h6 className="mb-3">Vendor profile</h6>

          <div className="vendorsModalLayout">
            <section className="vendorsModalSection">
              <div className="vendorsModalSectionHeader">
                <div>
                  <h6>Vendor profile</h6>
                  <p>Basic supplier identity, contacts, and where the record belongs.</p>
                </div>
              </div>

              <div className="vendorsModalFieldGrid">
                <Form.Group className="mb-3">
                  <Form.Label>Campus</Form.Label>
                  <Form.Select name="branch" value={formState.branch} onChange={handleFormChange}>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.branchName}
                      </option>
                    ))}
                    <option value="General">General</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Vendor status</Form.Label>
                  <Form.Select name="vendorStatus" value={formState.vendorStatus} onChange={handleFormChange}>
                    {VENDOR_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Vendor name</Form.Label>
                  <Form.Control
                    name="vendorName"
                    value={formState.vendorName}
                    onChange={handleFormChange}
                    placeholder="e.g. Landlord, Water supplier"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contact person</Form.Label>
                  <Form.Control
                    name="vendorContactPerson"
                    value={formState.vendorContactPerson}
                    onChange={handleFormChange}
                    placeholder="Primary person we talk to"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Service line</Form.Label>
                  <Form.Control
                    name="vendorServices"
                    value={formState.vendorServices}
                    onChange={handleFormChange}
                    placeholder="e.g. Rent, Internet, Cleaning"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Start date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="commDate"
                    value={formState.commDate}
                    onChange={handleFormChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    name="vendorEmail"
                    value={formState.vendorEmail}
                    onChange={handleFormChange}
                    placeholder="accounts@vendor.com"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    name="vendorPhone"
                    value={formState.vendorPhone}
                    onChange={handleFormChange}
                    placeholder="07..."
                  />
                </Form.Group>

                <Form.Group className="mb-3 vendorsModalFieldWide">
                  <Form.Label>Service description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="serviceDesc"
                    value={formState.serviceDesc}
                    onChange={handleFormChange}
                    placeholder="What they provide and any notes the team should know."
                  />
                </Form.Group>
              </div>
            </section>

            <section className="vendorsModalSection">
              <div className="vendorsModalSectionHeader">
                <div>
                  <h6>Payout profile</h6>
                  <p>Save how you will settle the vendor so the record stays payment-ready.</p>
                </div>
              </div>

              <div className="vendorsModalFieldGrid">
                <Form.Group className="mb-3 vendorsModalFieldWide">
                  <Form.Label>Payment method</Form.Label>
                  <Form.Select
                    name="vendorPaymentMethod"
                    value={formState.vendorPaymentMethod}
                    onChange={handleFormChange}
                  >
                    {PAYMENT_METHOD_OPTIONS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {formState.vendorPaymentMethod === 'Bank Transfer' && (
                  <Form.Group className="mb-3 vendorsModalFieldWide">
                    <Form.Label>Bank name</Form.Label>
                    <Form.Select
                      value={formState.vendorPaymentBankId}
                      onChange={handleBankSelect}
                      disabled={loadingBanks}
                    >
                      <option value="">{loadingBanks ? 'Loading banks...' : 'Select bank from Lotus'}</option>
                      {bankOptions.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name}{bank.code ? ` • ${bank.code}` : ''}
                        </option>
                      ))}
                    </Form.Select>
                    {bankLoadError && <small className="text-danger d-block mt-2">{bankLoadError}</small>}
                    <small className="text-muted d-block mt-2">
                      The bank list is pulled from Lotus so payout data stays consistent.
                    </small>
                  </Form.Group>
                )}

                <Form.Group className="mb-3 vendorsModalFieldWide">
                  <Form.Label>
                  {formState.vendorPaymentMethod === 'Bank Transfer'
                      ? 'Bank account number'
                      : formState.vendorPaymentMethod === 'MPESA'
                        ? 'MPESA phone number'
                        : formState.vendorPaymentMethod === 'Paybill'
                          ? 'Paybill details'
                          : formState.vendorPaymentMethod === 'Till Number'
                            ? 'Till number'
                            : 'Payout reference'}
                  </Form.Label>
                  {formState.vendorPaymentMethod === 'Paybill' ? (
                    <div className="vendorPaybillFields">
                      <Form.Control
                        className="mb-2"
                        name="vendorPaymentPaybillNumber"
                        value={formState.vendorPaymentPaybillNumber}
                        onChange={handleFormChange}
                        placeholder="Paybill number"
                      />
                      <Form.Control
                        name="vendorPaymentPaybillAccountNumber"
                        value={formState.vendorPaymentPaybillAccountNumber}
                        onChange={handleFormChange}
                        placeholder="Paybill account number"
                      />
                    </div>
                  ) : formState.vendorPaymentMethod === 'Till Number' ? (
                    <Form.Control
                      name="vendorPaymentTillNumber"
                      value={formState.vendorPaymentTillNumber}
                      onChange={handleFormChange}
                      placeholder="Till number"
                    />
                  ) : (
                    <Form.Control
                      name={formState.vendorPaymentMethod === 'MPESA' ? 'vendorPaymentPhone' : 'vendorPaymentReference'}
                      value={
                        formState.vendorPaymentMethod === 'MPESA'
                          ? formState.vendorPaymentPhone
                          : formState.vendorPaymentReference
                      }
                      onChange={handleFormChange}
                      placeholder={
                        formState.vendorPaymentMethod === 'Bank Transfer'
                          ? 'Vendor bank account number'
                          : formState.vendorPaymentMethod === 'MPESA'
                            ? '07...'
                            : 'Bank slip, cheque, or payment hint'
                      }
                    />
                  )}
                </Form.Group>

                <Form.Group className="mb-0 vendorsModalFieldWide">
                  <Form.Label>Payment notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="paymentDetails"
                    value={formState.paymentDetails}
                    onChange={handleFormChange}
                    placeholder="Optional notes for settlements, invoicing, or exceptions."
                  />
                </Form.Group>
              </div>

              <div className="vendorsModalNote">
                The payout profile is what we will use later when this vendor is settled. Keep it clean and complete now.
              </div>
            </section>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={closeModal} disabled={submitting}>
            Close
          </Button>
          <Button variant="dark" onClick={handleSubmitVendor} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              'Save vendor'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={detailsModalShow} onHide={closeDetailsModal} centered size="lg" dialogClassName="vendorsModalDialog">
        <Modal.Header closeButton>
          <Modal.Title>Vendor details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVendor ? (
            <div className="opsModalPreview">
              <div className="opsModalPreviewRow">
                <span>
                  <FontAwesomeIcon icon={faCircleInfo} /> Vendor
                </span>
                <strong>{selectedVendor.vendorName || 'Vendor'}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Campus</span>
                <strong>
                  {selectedVendor.branch === 'General'
                    ? 'General'
                    : branchMap[selectedVendor.branch] || 'Campus not set'}
                </strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Contact person</span>
                <strong>{selectedVendor.vendorContactPerson || 'Not set'}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Service line</span>
                <strong>{selectedVendor.vendorServices || '—'}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Status</span>
                <strong>{normalizeVendorStatus(selectedVendor.vendorStatus || selectedVendor.status)}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Payout method</span>
                <strong>{getVendorPayoutSummary(selectedVendor).method}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Contact</span>
                <strong>{selectedVendor.vendorPhone || selectedVendor.vendorEmail || 'Not set'}</strong>
              </div>
              <div className="opsModalCopyBlock">
                <span>Payout readiness</span>
                <p>
                  {getVendorPayoutSummary(selectedVendor).ready
                    ? 'This vendor is ready for payouts.'
                    : 'This vendor still needs payout details before settling payments.'}
                </p>
              </div>
              <div className="opsModalCopyBlock">
                <span>Service notes</span>
                <p>{selectedVendor.serviceDesc || 'No extra service notes yet.'}</p>
              </div>
              <div className="opsModalCopyBlock">
                <span>Payout profile</span>
                <p>
                  {[
                    getVendorPayoutSummary(selectedVendor).method,
                    selectedVendor.vendorPaymentBankName,
                    selectedVendor.vendorPaymentReference || selectedVendor.vendorPaymentPhone
                  ]
                    .filter(Boolean)
                    .join(' • ') || 'No payout profile added yet.'}
                </p>
              </div>
              <div className="opsModalCopyBlock">
                <span>Payment notes</span>
                <p>{selectedVendor.paymentDetails || 'No payment notes added yet.'}</p>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={closeDetailsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default VendorsComp;
